/**
 * TanStack Query hooks for monitored account CRUD operations.
 *
 * Query key: ['accounts']
 *
 * Error handling (Pattern 8.3 / NFR-RESIL-FE-03):
 *   - 400 responses: field validation errors are parsed and re-thrown as
 *     AccountApiError so AccountModal can display field-level messages
 *   - 409 responses: conflict error is re-thrown as AccountApiError with
 *     a human-readable message (duplicate accountId)
 *   - Other non-2xx: generic Error is thrown
 *
 * Security (SECURITY-01 / NFR-SEC-FE-01):
 *   - No credentials, tokens, or secrets are included in request bodies or headers
 *   - IAM Role ARN is user-supplied configuration, not a secret
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { MonitoredAccount, MonitoredAccountRequest, ApiErrorResponse } from '@/types';

// ── Error type ────────────────────────────────────────────────────────────────

export class AccountApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly fieldErrors?: Record<string, string>,
  ) {
    super(message);
    this.name = 'AccountApiError';
  }
}

// ── Fetch helpers ─────────────────────────────────────────────────────────────

async function handleAccountResponse(res: Response): Promise<void> {
  if (res.ok) return;

  let body: ApiErrorResponse | null = null;
  try {
    body = (await res.json()) as ApiErrorResponse;
  } catch {
    // Body may not be JSON — fall through to generic error
  }

  if (res.status === 400 && body?.errors) {
    const fieldErrors: Record<string, string> = {};
    for (const e of body.errors) {
      fieldErrors[e.field] = e.message;
    }
    throw new AccountApiError('Validation failed', 400, fieldErrors);
  }

  if (res.status === 409) {
    throw new AccountApiError(
      body?.error ?? 'Account ID already exists',
      409,
    );
  }

  throw new AccountApiError(
    body?.error ?? `Request failed: ${res.status} ${res.statusText}`,
    res.status,
  );
}

async function fetchAccounts(): Promise<MonitoredAccount[]> {
  const res = await fetch('/api/accounts');
  if (!res.ok) {
    throw new Error(`Failed to load accounts: ${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<MonitoredAccount[]>;
}

async function createAccount(request: MonitoredAccountRequest): Promise<MonitoredAccount> {
  const res = await fetch('/api/accounts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  await handleAccountResponse(res);
  return res.json() as Promise<MonitoredAccount>;
}

async function updateAccount({
  id,
  request,
}: {
  id: number;
  request: MonitoredAccountRequest;
}): Promise<MonitoredAccount> {
  const res = await fetch(`/api/accounts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  await handleAccountResponse(res);
  return res.json() as Promise<MonitoredAccount>;
}

async function deleteAccount(id: number): Promise<void> {
  const res = await fetch(`/api/accounts/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    throw new AccountApiError(
      `Failed to delete account: ${res.status} ${res.statusText}`,
      res.status,
    );
  }
}

// ── Query hooks ───────────────────────────────────────────────────────────────

/** Fetches all monitored accounts. */
export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: fetchAccounts,
  });
}

/** Mutation to create a new monitored account. */
export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAccount,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/** Mutation to update an existing monitored account. */
export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAccount,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/** Mutation to delete a monitored account by ID. */
export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
